import React, { useEffect, useState, createElement, Component } from 'react';
import { Users, GraduationCap, PartyPopper, Building2, Briefcase, Handshake, RefreshCw, UserPlus, BarChart3, Bell, Search, Filter, Download, Trash2, CheckCircle2, Clock, Archive, Eye, X, ChevronDown, Calendar, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { getStats, getSubmissions, updateSubmissionStatus, deleteSubmission, exportToCSV, seedDemoData, type Submission, type AtelierSubmission, type FestivalSubmission, type EmployeurSubmission, type RecruterSubmission, type PartenariatSubmission, type RecyclageSubmission, type MembreSubmission, type FormateurSubmission, type BenevoleSubmission } from '../services/databaseService';
type TabType = 'overview' | 'ateliers' | 'festivals' | 'employeurs' | 'recrutements' | 'partenariats' | 'recyclages' | 'membres' | 'formateurs' | 'benevoles';
const statusColors = {
  nouveau: 'bg-blue-100 text-blue-800',
  en_cours: 'bg-yellow-100 text-yellow-800',
  traite: 'bg-green-100 text-green-800',
  archive: 'bg-gray-100 text-gray-800'
};
const statusLabels = {
  nouveau: 'Nouveau',
  en_cours: 'En cours',
  traite: 'Traité',
  archive: 'Archivé'
};
export function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [stats, setStats] = useState(getStats());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    // Charger les données de démo au premier chargement
    seedDemoData();
    setStats(getStats());
  }, [refreshKey]);
  const refresh = () => {
    setRefreshKey(k => k + 1);
    setStats(getStats());
  };
  const handleStatusChange = (type: string, id: string, newStatus: Submission['status']) => {
    updateSubmissionStatus(type as any, id, newStatus);
    refresh();
    if (selectedSubmission?.id === id) {
      setSelectedSubmission({
        ...selectedSubmission,
        status: newStatus
      });
    }
  };
  const handleDelete = (type: string, id: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette soumission ?')) {
      deleteSubmission(type as any, id);
      refresh();
      setSelectedSubmission(null);
    }
  };
  const handleExport = (type: string) => {
    const csv = exportToCSV(type as any);
    if (!csv) {
      alert('Aucune donnée à exporter');
      return;
    }
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `adcc_${type}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };
  const tabs = [{
    id: 'overview',
    label: "Vue d'ensemble",
    icon: BarChart3
  }, {
    id: 'formateurs',
    label: 'Formateurs',
    icon: GraduationCap,
    count: stats.formateurs
  }, {
    id: 'benevoles',
    label: 'Bénévoles',
    icon: Users,
    count: stats.benevoles
  }, {
    id: 'ateliers',
    label: 'Ateliers',
    icon: GraduationCap,
    count: stats.ateliers
  }, {
    id: 'festivals',
    label: 'Festival',
    icon: PartyPopper,
    count: stats.festivals
  }, {
    id: 'employeurs',
    label: 'Employeurs',
    icon: Building2,
    count: stats.employeurs
  }, {
    id: 'recrutements',
    label: 'Recrutements',
    icon: Briefcase,
    count: stats.recrutements
  }, {
    id: 'partenariats',
    label: 'Partenariats',
    icon: Handshake,
    count: stats.partenariats
  }, {
    id: 'recyclages',
    label: 'Recyclages',
    icon: RefreshCw,
    count: stats.recyclages
  }, {
    id: 'membres',
    label: 'Membres',
    icon: UserPlus,
    count: stats.membres
  }];
  return <div className="min-h-screen bg-gray-100" data-id="element-437">
      {/* Header */}
      <header className="bg-[#2C3E50] text-white shadow-lg" data-id="element-438">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-439">
          <div className="flex items-center justify-between" data-id="element-440">
            <div className="flex items-center gap-4" data-id="element-441">
              <img src="https://cdn.magicpatterns.com/uploads/qCb6hamLArrr2SMb6t4UDJ/ADCC_logo.png" alt="ADCC" className="h-12 w-auto" data-id="element-442" />
              <div data-id="element-443">
                <h1 className="text-xl font-bold" data-id="element-444">Dashboard Administration</h1>
                <p className="text-sm text-gray-300" data-id="element-445">
                  Gestion des soumissions de formulaires
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-446">
              <div className="relative" data-id="element-447">
                <Bell className="w-6 h-6 text-gray-300 hover:text-white cursor-pointer" data-id="element-448" />
                {stats.nouveaux > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center" data-id="element-449">
                    {stats.nouveaux}
                  </span>}
              </div>
              <Button variant="ghost" className="text-white hover:bg-white/10" onClick={refresh} data-id="element-450">
                <RefreshCw className="w-4 h-4 mr-2" data-id="element-451" />
                Actualiser
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-452">
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6 overflow-x-auto" data-id="element-453">
          <div className="flex border-b border-gray-200" data-id="element-454">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-[#1E88E5] text-[#1E88E5]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-455">
                <tab.icon className="w-4 h-4" data-id="element-456" />
                {tab.label}
                {tab.count !== undefined && <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id ? 'bg-[#1E88E5] text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-457">
                    {tab.count}
                  </span>}
              </button>)}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'overview' ? <OverviewTab stats={stats} setActiveTab={setActiveTab} data-id="element-458" /> : <DataTable type={activeTab} searchTerm={searchTerm} setSearchTerm={setSearchTerm} statusFilter={statusFilter} setStatusFilter={setStatusFilter} onView={setSelectedSubmission} onStatusChange={handleStatusChange} onDelete={handleDelete} onExport={handleExport} data-id="element-459" />}
      </div>

      {/* Detail Modal */}
      {selectedSubmission && <DetailModal submission={selectedSubmission} onClose={() => setSelectedSubmission(null)} onStatusChange={handleStatusChange} onDelete={handleDelete} data-id="element-460" />}
    </div>;
}
// Overview Tab Component
function OverviewTab({
  stats,
  setActiveTab
}: {
  stats: ReturnType<typeof getStats>;
  setActiveTab: (tab: TabType) => void;
}) {
  const cards = [{
    label: 'Candidatures Formateurs',
    value: stats.formateurs,
    icon: GraduationCap,
    color: 'bg-cyan-500',
    tab: 'formateurs' as TabType
  }, {
    label: 'Candidatures Bénévoles',
    value: stats.benevoles,
    icon: Users,
    color: 'bg-rose-500',
    tab: 'benevoles' as TabType
  }, {
    label: 'Inscriptions Ateliers',
    value: stats.ateliers,
    icon: GraduationCap,
    color: 'bg-blue-500',
    tab: 'ateliers' as TabType
  }, {
    label: 'Participations Festival',
    value: stats.festivals,
    icon: PartyPopper,
    color: 'bg-purple-500',
    tab: 'festivals' as TabType
  }, {
    label: 'Demandes Employeurs',
    value: stats.employeurs,
    icon: Building2,
    color: 'bg-green-500',
    tab: 'employeurs' as TabType
  }, {
    label: 'Demandes Recrutement',
    value: stats.recrutements,
    icon: Briefcase,
    color: 'bg-amber-500',
    tab: 'recrutements' as TabType
  }, {
    label: 'Propositions Partenariat',
    value: stats.partenariats,
    icon: Handshake,
    color: 'bg-indigo-500',
    tab: 'partenariats' as TabType
  }, {
    label: 'Demandes Recyclage',
    value: stats.recyclages,
    icon: RefreshCw,
    color: 'bg-teal-500',
    tab: 'recyclages' as TabType
  }, {
    label: 'Demandes Adhésion',
    value: stats.membres,
    icon: UserPlus,
    color: 'bg-pink-500',
    tab: 'membres' as TabType
  }];
  return <div className="space-y-6" data-id="element-461">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-462">
        <Card className="bg-gradient-to-br from-[#1E88E5] to-[#1565C0] text-white" data-id="element-463">
          <CardContent className="p-6" data-id="element-464">
            <div className="flex items-center justify-between" data-id="element-465">
              <div data-id="element-466">
                <p className="text-blue-100 text-sm" data-id="element-467">Total Soumissions</p>
                <p className="text-4xl font-bold mt-1" data-id="element-468">{stats.total}</p>
              </div>
              <BarChart3 className="w-12 h-12 text-blue-200" data-id="element-469" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white" data-id="element-470">
          <CardContent className="p-6" data-id="element-471">
            <div className="flex items-center justify-between" data-id="element-472">
              <div data-id="element-473">
                <p className="text-green-100 text-sm" data-id="element-474">Nouvelles demandes</p>
                <p className="text-4xl font-bold mt-1" data-id="element-475">{stats.nouveaux}</p>
              </div>
              <Bell className="w-12 h-12 text-green-200" data-id="element-476" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white" data-id="element-477">
          <CardContent className="p-6" data-id="element-478">
            <div className="flex items-center justify-between" data-id="element-479">
              <div data-id="element-480">
                <p className="text-purple-100 text-sm" data-id="element-481">Ateliers Mars 2026</p>
                <p className="text-4xl font-bold mt-1" data-id="element-482">{stats.ateliers}</p>
              </div>
              <GraduationCap className="w-12 h-12 text-purple-200" data-id="element-483" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white" data-id="element-484">
          <CardContent className="p-6" data-id="element-485">
            <div className="flex items-center justify-between" data-id="element-486">
              <div data-id="element-487">
                <p className="text-amber-100 text-sm" data-id="element-488">Recrutements</p>
                <p className="text-4xl font-bold mt-1" data-id="element-489">{stats.recrutements}</p>
              </div>
              <Briefcase className="w-12 h-12 text-amber-200" data-id="element-490" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-491">
        {cards.map(card => <Card key={card.label} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveTab(card.tab)} data-id="element-492">
            <CardContent className="p-6" data-id="element-493">
              <div className="flex items-center gap-4" data-id="element-494">
                <div className={`p-3 ${card.color} rounded-lg`} data-id="element-495">
                  <card.icon className="w-6 h-6 text-white" data-id="element-496" />
                </div>
                <div data-id="element-497">
                  <p className="text-2xl font-bold text-gray-900" data-id="element-498">
                    {card.value}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-499">{card.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>)}
      </div>

      {/* Quick Info */}
      <Card data-id="element-500">
        <CardContent className="p-6" data-id="element-501">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-502">Informations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm" data-id="element-503">
            <div className="bg-blue-50 p-4 rounded-lg" data-id="element-504">
              <h4 className="font-semibold text-blue-900 mb-2" data-id="element-505">💡 Astuce</h4>
              <p className="text-blue-700" data-id="element-506">
                Cliquez sur une catégorie pour voir les détails des soumissions.
                Vous pouvez filtrer par statut et exporter les données en CSV.
              </p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg" data-id="element-507">
              <h4 className="font-semibold text-amber-900 mb-2" data-id="element-508">
                ⚠️ Note importante
              </h4>
              <p className="text-amber-700" data-id="element-509">
                Les données sont actuellement stockées localement dans votre
                navigateur. Pour une solution permanente, connectez une base de
                données externe.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}
// Data Table Component
function DataTable({
  type,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  onView,
  onStatusChange,
  onDelete,
  onExport
}: {
  type: TabType;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  onView: (submission: Submission) => void;
  onStatusChange: (type: string, id: string, status: Submission['status']) => void;
  onDelete: (type: string, id: string) => void;
  onExport: (type: string) => void;
}) {
  const typeMap: Record<TabType, string> = {
    overview: '',
    ateliers: 'atelier',
    festivals: 'festival',
    employeurs: 'employeur',
    recrutements: 'recruter',
    partenariats: 'partenariat',
    recyclages: 'recyclage',
    membres: 'membre',
    formateurs: 'formateur',
    benevoles: 'benevole'
  };
  const dbType = typeMap[type];
  const submissions = getSubmissions(dbType as any) as Submission[];
  const filtered = submissions.filter(s => {
    const matchesSearch = searchTerm === '' || JSON.stringify(s).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const getDisplayName = (s: Submission): string => {
    if ('nom' in s && 'prenom' in s) return `${s.nom} ${s.prenom}`;
    if ('nomContact' in s) return s.nomContact;
    if ('nomEmployeur' in s) return s.nomEmployeur;
    if ('organisation' in s) return s.organisation;
    return 'N/A';
  };
  const getEmail = (s: Submission): string => {
    if ('email' in s) return s.email;
    if ('emailEmployeur' in s) return s.emailEmployeur;
    return 'N/A';
  };
  const getPhone = (s: Submission): string => {
    if ('telephone' in s) return s.telephone;
    if ('telephoneEmployeur' in s) return s.telephoneEmployeur;
    return 'N/A';
  };
  return <div className="space-y-4" data-id="element-510">
      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4" data-id="element-511">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-512">
          <div className="flex items-center gap-4 w-full md:w-auto" data-id="element-513">
            <div className="relative flex-1 md:w-64" data-id="element-514">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-515" />
              <input type="text" placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none" data-id="element-516" />
            </div>
            <div className="relative" data-id="element-517">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-518" />
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent outline-none appearance-none bg-white" data-id="element-519">
                <option value="all" data-id="element-520">Tous les statuts</option>
                <option value="nouveau" data-id="element-521">Nouveau</option>
                <option value="en_cours" data-id="element-522">En cours</option>
                <option value="traite" data-id="element-523">Traité</option>
                <option value="archive" data-id="element-524">Archivé</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-525" />
            </div>
          </div>
          <Button variant="secondary" onClick={() => onExport(dbType)} className="gap-2" data-id="element-526">
            <Download className="w-4 h-4" data-id="element-527" />
            Exporter CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-528">
        {filtered.length === 0 ? <div className="p-12 text-center" data-id="element-529">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-530">
              <Search className="w-8 h-8 text-gray-400" data-id="element-531" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-532">
              Aucune soumission
            </h3>
            <p className="text-gray-500" data-id="element-533">
              {searchTerm || statusFilter !== 'all' ? 'Aucun résultat ne correspond à vos critères de recherche.' : 'Les soumissions apparaîtront ici une fois reçues.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-534">
            <table className="w-full" data-id="element-535">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-536">
                <tr data-id="element-537">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-538">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-539">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-540">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-541">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-542">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-543">
                {filtered.map(submission => <tr key={submission.id} className="hover:bg-gray-50" data-id="element-544">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-545">
                      <div className="font-medium text-gray-900" data-id="element-546">
                        {getDisplayName(submission)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-547">
                      <div className="text-sm text-gray-500" data-id="element-548">
                        {getEmail(submission)}
                      </div>
                      <div className="text-sm text-gray-400" data-id="element-549">
                        {getPhone(submission)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-550">
                      {new Date(submission.createdAt).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-551">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[submission.status]}`} data-id="element-552">
                        {statusLabels[submission.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-553">
                      <div className="flex items-center justify-end gap-2" data-id="element-554">
                        <button onClick={() => onView(submission)} className="p-1 text-gray-400 hover:text-[#1E88E5] transition-colors" title="Voir les détails" data-id="element-555">
                          <Eye className="w-5 h-5" data-id="element-556" />
                        </button>
                        <button onClick={() => onDelete(dbType, submission.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Supprimer" data-id="element-557">
                          <Trash2 className="w-5 h-5" data-id="element-558" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}
// Detail Modal Component
function DetailModal({
  submission,
  onClose,
  onStatusChange,
  onDelete
}: {
  submission: Submission;
  onClose: () => void;
  onStatusChange: (type: string, id: string, status: Submission['status']) => void;
  onDelete: (type: string, id: string) => void;
}) {
  const typeLabels: Record<string, string> = {
    atelier: 'Inscription Atelier',
    festival: 'Participation Festival',
    employeur: 'Demande Employeur',
    recruter: 'Demande Recrutement',
    partenariat: 'Proposition Partenariat',
    recyclage: 'Demande Recyclage',
    membre: 'Demande Adhésion',
    formateur: 'Candidature Formateur',
    benevole: 'Candidature Bénévole'
  };
  const renderField = (label: string, value: string | string[] | undefined) => {
    if (!value || Array.isArray(value) && value.length === 0) return null;
    return <div className="py-3 border-b border-gray-100 last:border-0" data-id="element-559">
        <dt className="text-sm font-medium text-gray-500" data-id="element-560">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900" data-id="element-561">
          {Array.isArray(value) ? value.join(', ') : value}
        </dd>
      </div>;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" data-id="element-562">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-8 relative max-h-[90vh] overflow-y-auto" data-id="element-563">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10" data-id="element-564">
          <div data-id="element-565">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-566">
              {typeLabels[submission.type] || 'Détails'}
            </h2>
            <p className="text-sm text-gray-500" data-id="element-567">
              Reçu le{' '}
              {new Date(submission.createdAt).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors" data-id="element-568">
            <X className="w-6 h-6" data-id="element-569" />
          </button>
        </div>

        <div className="p-6" data-id="element-570">
          {/* Status Actions */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg" data-id="element-571">
            <label className="text-sm font-medium text-gray-700 mb-2 block" data-id="element-572">
              Changer le statut
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-573">
              {(['nouveau', 'en_cours', 'traite', 'archive'] as const).map(status => <button key={status} onClick={() => onStatusChange(submission.type, submission.id, status)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${submission.status === status ? statusColors[status] + ' ring-2 ring-offset-2 ring-gray-300' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-574">
                    {statusLabels[status]}
                  </button>)}
            </div>
          </div>

          {/* Fields */}
          <dl className="space-y-1" data-id="element-575">
            {/* Common fields based on type */}
            {'nom' in submission && renderField('Nom', submission.nom)}
            {'prenom' in submission && renderField('Prénom', submission.prenom)}
            {'nomContact' in submission && renderField('Nom du contact', submission.nomContact)}
            {'nomEmployeur' in submission && renderField('Nom employeur', submission.nomEmployeur)}
            {'organisation' in submission && renderField('Organisation', submission.organisation)}
            {'email' in submission && renderField('Email', submission.email)}
            {'emailEmployeur' in submission && renderField('Email', submission.emailEmployeur)}
            {'telephone' in submission && renderField('Téléphone', submission.telephone)}
            {'telephoneEmployeur' in submission && renderField('Téléphone', submission.telephoneEmployeur)}
            {'ville' in submission && renderField('Ville', submission.ville)}
            {'villeEmployeur' in submission && renderField('Ville', submission.villeEmployeur)}
            {'quartier' in submission && renderField('Quartier', submission.quartier)}
            {'dateNaissance' in submission && renderField('Date de naissance', submission.dateNaissance)}
            {'sexe' in submission && renderField('Sexe', submission.sexe === 'F' ? 'Femme' : 'Homme')}
            {'situation' in submission && renderField('Situation professionnelle', submission.situation)}
            {'niveauEtudes' in submission && renderField("Niveau d'études", submission.niveauEtudes)}
            {'formationSouhaitee' in submission && renderField('Formation souhaitée', submission.formationSouhaitee)}
            {'formationAutre' in submission && renderField('Autre formation', submission.formationAutre)}
            {'source' in submission && renderField('Source', submission.source)}
            {'motivations' in submission && renderField('Motivations', submission.motivations)}
            {'profil' in submission && renderField('Profil', submission.profil)}
            {'participation' in submission && renderField('Type de participation', submission.participation)}
            {'message' in submission && renderField('Message', submission.message)}
            {'typeEmployeur' in submission && renderField("Type d'employeur", submission.typeEmployeur)}
            {'profilsRecherches' in submission && renderField('Profils recherchés', submission.profilsRecherches)}
            {'attentes' in submission && renderField('Attentes', submission.attentes)}
            {'typePersonnel' in submission && renderField('Type de personnel', submission.typePersonnel)}
            {'typeContrat' in submission && renderField('Type de contrat', submission.typeContrat)}
            {'dateDebut' in submission && renderField('Date de début', submission.dateDebut)}
            {'description' in submission && renderField('Description', submission.description)}
            {'typeOrganisation' in submission && renderField("Type d'organisation", submission.typeOrganisation)}
            {'fonction' in submission && renderField('Fonction', submission.fonction)}
            {'typePartenariat' in submission && renderField('Type de partenariat', submission.typePartenariat)}
            {'proposition' in submission && renderField('Proposition', submission.proposition)}
            {'nomEmploye' in submission && renderField("Nom de l'employé", submission.nomEmploye)}
            {'posteActuel' in submission && renderField('Poste actuel', submission.posteActuel)}
            {'anciennete' in submission && renderField('Ancienneté', submission.anciennete)}
            {'pointsFormation' in submission && renderField('Points de formation', submission.pointsFormation)}
            {'autresPoints' in submission && renderField('Autres points', submission.autresPoints)}
            {'periodeSouhaitee' in submission && renderField('Période souhaitée', submission.periodeSouhaitee)}
            {'dureeSouhaitee' in submission && renderField('Durée souhaitée', submission.dureeSouhaitee)}
            {'observations' in submission && renderField('Observations', submission.observations)}
            {'profession' in submission && renderField('Profession', submission.profession)}
            {'typeMembre' in submission && renderField('Type de membre', submission.typeMembre)}
            {'contribution' in submission && renderField('Contribution', submission.contribution)}
            {'domaine' in submission && renderField("Domaine d'expertise", submission.domaine)}
            {'experience' in submission && renderField('Expérience', submission.experience)}
            {'motivation' in submission && renderField('Motivation', submission.motivation)}
            {'disponibilite' in submission && renderField('Disponibilité', submission.disponibilite)}
          </dl>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between" data-id="element-576">
          <Button variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => onDelete(submission.type, submission.id)} data-id="element-577">
            <Trash2 className="w-4 h-4 mr-2" data-id="element-578" />
            Supprimer
          </Button>
          <Button onClick={onClose} data-id="element-579">Fermer</Button>
        </div>
      </div>
    </div>;
}